<template>
  <div
    class="calendar-event border rounded p-3 mb-3 bg-light"
    :class="{ 'calendar-event--missing-data': hasMissingLinkData }"
  >
    <h6 class="font-weight-bold mb-2">
      <i class="icon-calendar mr-2 text-primary"></i>
      {{ eventTitle }}
    </h6>

    <ul class="list-unstyled mb-0 small">
      <li class="mb-1">
        <strong
          :class="
            startTime && new Date(startTime) < new Date()
              ? 'text-danger'
              : 'text-primary'
          "
        >
          <i class="icon-clock mr-1"></i>
          {{ $t('lang.calendar.event_start') }}:
        </strong>
        <span v-if="startTime">{{ formatDateTime(startTime) }}</span>
        <span v-else>{{ event.start_time || event.startTime || '—' }}</span>
      </li>
      <li v-if="endTime" class="mb-1">
        <strong class="text-secondary"><i class="icon-clock mr-1"></i>{{ $t('lang.calendar.event_end') }}:</strong>
        {{ formatDateTime(endTime) }}
      </li>
      <li class="mb-1">
        <strong class="text-secondary">calendar_event_id:</strong>
        <span :class="{ 'text-danger': event.calendar_event_id == null }">
          {{ event.calendar_event_id != null ? event.calendar_event_id : '—' }}
        </span>
      </li>
      <li class="mb-1">
        <strong class="text-secondary">link_event_id:</strong>
        <span :class="{ 'text-danger': event.link_event_id == null }">
          {{ event.link_event_id != null ? event.link_event_id : '—' }}
        </span>
      </li>
      <li class="mb-1">
        <strong class="text-secondary">link_id:</strong>
        <span v-if="event.link_id != null">
          <a
            :href="opportunityUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="text-primary ml-1"
          >
            {{ event.link_id }}
          </a>
        </span>
        <span v-else class="text-danger">—</span>
      </li>
      <li v-if="eventLocation" class="mb-1">
        <i class="icon-location mr-1 text-secondary"></i>
        {{ eventLocation }}
      </li>
      <li v-if="organizerText" class="mb-1">
        <strong class="text-secondary">{{ $t('lang.calendar.event_organizer') }}:</strong>
        {{ organizerText }}
      </li>
      <li v-if="attendeesList.length" class="mb-1">
        <strong class="text-secondary">{{ $t('lang.calendar.event_participants') }}:</strong>
        <ul class="list-unstyled ml-3 mt-1 mb-0">
          <li
            v-for="(a, idx) in attendeesList"
            :key="idx"
            class="mb-0"
          >
            {{ a.name || a.email }} ({{ a.email }}) —
            <span class="badge badge-sm" :class="responseBadgeClass(a.responseStatus)">
              {{ a.responseStatus || '—' }}
            </span>
          </li>
        </ul>
      </li>
      <li v-if="joinUrl" class="mb-1">
        <a
          :href="joinUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="text-primary"
        >
          <i class="icon-video-camera mr-1"></i>
          {{ $t('lang.label.joinTheMeeting') }}
        </a>
      </li>
      <li v-if="event.webLink" class="mb-1">
        <a
          :href="event.webLink"
          target="_blank"
          rel="noopener noreferrer"
          class="text-primary"
        >
          <i class="icon-link mr-1"></i>
          {{ $t('lang.calendar.event_view_in_calendar') }}
        </a>
      </li>
      <li v-if="event.bodyPreview" class="mb-1">
        <strong class="text-secondary">{{ $t('lang.calendar.event_summary') }}:</strong>
        <span class="d-block text-muted" style="white-space: pre-wrap;">{{ event.bodyPreview }}</span>
      </li>
      <li v-if="event.showAs" class="mb-1">
        <strong class="text-secondary">{{ $t('lang.calendar.event_status') }}:</strong>
        {{ event.showAs }}
      </li>
      <li v-if="event.isCancelled" class="mb-1">
        <span class="badge badge-danger">{{ $t('lang.calendar.event_cancelled') }}</span>
      </li>
    </ul>
  </div>
</template>

<script>
export default {
  name: 'CalendarEvent',
  props: {
    event: {
      type: Object,
      required: true,
    },
  },
  computed: {
    hasMissingLinkData() {
      return (
        this.event.calendar_event_id == null ||
        this.event.link_event_id == null ||
        this.event.link_id == null
      )
    },
    eventTitle() {
      return (
        this.event.subject ||
        this.event.name ||
        this.event.title ||
        this.event.summary ||
        this.$t('lang.calendar.event_no_title')
      )
    },
    startTime() {
      return this.event.start_time || this.event.startTime || this.event.start
    },
    endTime() {
      return this.event.end_time || this.event.endTime || this.event.end
    },
    eventLocation() {
      if (
        this.event.location &&
        typeof this.event.location === 'object' &&
        this.event.location.address
      ) {
        return this.event.location.address
      }
      if (typeof this.event.location === 'string') return this.event.location
      return null
    },
    joinUrl() {
      if (
        this.event.location &&
        typeof this.event.location === 'object' &&
        this.event.location.join_url
      ) {
        return this.event.location.join_url
      }
      return this.event.join_url || this.event.conference_url || null
    },
    opportunityUrl() {
      if (!this.event.link_id) return '#'
      const base = typeof process !== 'undefined' && process.env && process.env.VUE_APP_APP_URL
        ? process.env.VUE_APP_APP_URL
        : (typeof window !== 'undefined' ? window.location.origin : 'https://app.oliverlist.com')
      return `${base.replace(/\/$/, '')}/opportunities/${this.event.link_id}`
    },
    organizerText() {
      const o = this.event.organizer
      if (!o) return ''
      if (o.name && o.email) return `${o.name} (${o.email})`
      return o.email || o.name || ''
    },
    attendeesList() {
      const a = this.event.attendees
      return Array.isArray(a) ? a : []
    },
  },
  methods: {
    formatDateTime(value) {
      if (!value) return '—'
      const d = new Date(value)
      if (isNaN(d.getTime())) return value
      return d.toLocaleString(undefined, {
        dateStyle: 'short',
        timeStyle: 'short',
      })
    },
    responseBadgeClass(status) {
      if (!status) return 'badge-secondary'
      const s = (status || '').toLowerCase()
      if (s === 'accepted') return 'badge-success'
      if (s === 'declined') return 'badge-danger'
      if (s === 'tentative') return 'badge-warning'
      return 'badge-secondary'
    },
  },
}
</script>

<style scoped>
.calendar-event {
  transition: background-color 0.15s ease;
}
.calendar-event:hover {
  background-color: #f0f0f0 !important;
}
.calendar-event--missing-data {
  border: 2px solid #dc3545 !important;
}
</style>
